import { useEffect, useState, useCallback } from "react";
import { FileText, Plus, History, Save, X, Download, Search, Calendar, AlertTriangle, RotateCcw } from "lucide-react";
import api, { API, getToken } from "@/lib/api";
import { toast } from "sonner";

function todayISO() { return new Date().toISOString().slice(0, 10); }
function fmtDateTime(iso) { try { return new Date(iso).toLocaleString(); } catch { return iso || "—"; } }

export function WaiverSettingsPanel() {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [historyVersion, setHistoryVersion] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/waiver-versions");
      setVersions(r.data.items || []);
    } catch { toast.error("Failed to load waiver versions"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const active = versions.find((v) => v.is_active);

  const startEdit = async () => {
    if (active) {
      try {
        const r = await api.get(`/admin/waiver-versions/${active.id}`);
        setDraft(r.data.text || "");
      } catch { setDraft(""); }
    } else { setDraft(""); }
    setEditing(true);
  };

  const save = async () => {
    const txt = draft.trim();
    if (!txt) { toast.error("Waiver text cannot be empty"); return; }
    setSaving(true);
    try {
      const r = await api.post("/admin/waiver-versions", { text: txt });
      toast.success(`Waiver version ${r.data.version_number} saved.`);
      setEditing(false); setDraft("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const restore = async (vid, num) => {
    if (!confirm(`Restore version ${num} as the new active waiver? (Original v${num} stays in history.)`)) return;
    try {
      const r = await api.post(`/admin/waiver-versions/${vid}/restore`);
      toast.success(`Restored as v${r.data.new_version_number}`);
      load();
    } catch { toast.error("Restore failed"); }
  };

  const viewVersion = async (vid) => {
    try {
      const r = await api.get(`/admin/waiver-versions/${vid}`);
      setHistoryVersion(r.data);
    } catch { toast.error("Failed to load version"); }
  };

  return (
    <section className="space-y-4" data-testid="admin-waiver-settings">
      {!loading && versions.length === 0 && !editing && (
        <div className="rounded-xl bg-amber-50 border border-amber-200 px-4 py-3 flex items-start gap-3" data-testid="waiver-banner-empty">
          <AlertTriangle className="w-5 h-5 text-amber-700 mt-0.5 shrink-0" />
          <div className="text-sm text-amber-900">
            <strong>No waiver text configured.</strong> Visitors are not signing waivers. Add waiver text below.
          </div>
        </div>
      )}

      <div className="rounded-2xl border border-slate-200 bg-white p-5">
        <div className="flex items-center gap-2 mb-3">
          <FileText className="w-5 h-5 text-amber-700" />
          <h3 className="font-bold text-slate-900">Active Waiver</h3>
          {active && <span className="ml-auto text-xs uppercase tracking-wide font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded">v{active.version_number} · active</span>}
        </div>
        {!editing ? (
          <>
            {active ? (
              <div className="rounded-xl border border-slate-100 bg-slate-50 p-4 text-sm text-slate-800" style={{ whiteSpace: "pre-wrap", wordWrap: "break-word", maxHeight: "300px", overflowY: "auto" }} data-testid="waiver-active-preview">
                {active.text_preview || ""}
              </div>
            ) : (
              <div className="text-sm text-slate-500 italic">No waiver yet. Tap "Edit waiver" to add the first version.</div>
            )}
            <button onClick={startEdit} className="mt-3 btn-touch inline-flex items-center gap-2 px-4 rounded-xl bg-amber-600 hover:bg-amber-700 text-white font-semibold" data-testid="waiver-edit-btn">
              <Plus className="w-4 h-4" /> {active ? "Edit waiver (creates new version)" : "Add waiver text"}
            </button>
          </>
        ) : (
          <div className="space-y-3" data-testid="waiver-editor">
            <div className="text-xs text-slate-500">Use blank lines between paragraphs. Line breaks and spacing are preserved exactly.</div>
            <textarea
              value={draft} onChange={(e) => setDraft(e.target.value)}
              rows={20}
              className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none focus:ring-2 focus:ring-amber-400 font-mono text-sm"
              style={{ minHeight: "400px" }}
              placeholder="Paste your waiver text here…"
              data-testid="waiver-editor-textarea"
            />
            <div className="flex items-center gap-2">
              <button onClick={save} disabled={saving || !draft.trim()}
                      className="btn-touch inline-flex items-center gap-2 px-4 rounded-xl bg-amber-600 hover:bg-amber-700 text-white font-semibold disabled:opacity-50"
                      data-testid="waiver-save-btn">
                <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save as new version"}
              </button>
              <button onClick={() => { setEditing(false); setDraft(""); }}
                      className="btn-touch inline-flex items-center gap-2 px-4 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-800 font-semibold"
                      data-testid="waiver-cancel-btn">
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-5">
        <div className="flex items-center gap-2 mb-3">
          <History className="w-5 h-5 text-slate-600" />
          <h3 className="font-bold text-slate-900">Version History</h3>
        </div>
        {versions.length === 0 ? (
          <div className="text-sm text-slate-500">No versions yet.</div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {versions.map((v) => (
              <li key={v.id} className="py-2 flex items-center gap-3" data-testid={`waiver-version-${v.version_number}`}>
                <div className="flex-1">
                  <div className="font-bold text-slate-900">Version {v.version_number} {v.is_active && <span className="ml-2 text-[10px] uppercase tracking-wide font-bold text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded border border-emerald-200">Active</span>}</div>
                  <div className="text-xs text-slate-500">{v.created_by || "—"} · {fmtDateTime(v.created_at)} · {v.signature_count} signature{v.signature_count === 1 ? "" : "s"}</div>
                </div>
                <button onClick={() => viewVersion(v.id)} className="text-xs text-slate-500 hover:text-slate-800 underline-offset-2 hover:underline">View</button>
                {!v.is_active && (
                  <button onClick={() => restore(v.id, v.version_number)} className="text-xs text-amber-700 hover:text-amber-900 inline-flex items-center gap-1"
                          data-testid={`waiver-restore-${v.version_number}`}>
                    <RotateCcw className="w-3 h-3" /> Restore
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      {historyVersion && (
        <div className="fixed inset-0 z-50 bg-black/40 p-4 flex items-center justify-center" onClick={() => setHistoryVersion(null)} data-testid="waiver-version-modal">
          <div className="bg-white rounded-3xl max-w-2xl w-full max-h-[85vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white">
              <h3 className="font-bold">Version {historyVersion.version_number}</h3>
              <button onClick={() => setHistoryVersion(null)} className="p-2 hover:bg-slate-100 rounded"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-3">
              <div className="text-xs text-slate-500">{fmtDateTime(historyVersion.created_at)} · {historyVersion.signature_count} signature{historyVersion.signature_count === 1 ? "" : "s"}</div>
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-800" style={{ whiteSpace: "pre-wrap", wordWrap: "break-word" }}>
                {historyVersion.text}
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

export function WaiverRecordsPanel() {
  const [date, setDate] = useState(todayISO());
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);

  const load = useCallback(async (d) => {
    setLoading(true);
    try {
      const r = await api.get("/admin/waiver-signatures/by-date", { params: { date: d } });
      setItems(r.data.items || []);
    } catch { toast.error("Failed to load signatures"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(date); }, [date, load]);

  const openDetail = async (sid) => {
    try {
      const r = await api.get(`/admin/waiver-signatures/${sid}`);
      setDetail(r.data);
    } catch { toast.error("Failed to load detail"); }
  };

  const downloadPdf = async (sid, name) => {
    try {
      const token = getToken();
      const res = await fetch(`${API}/admin/waiver-signatures/${sid}/pdf`,
        { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error("download failed");
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `Waiver_${(name || "Visitor").replace(/\s+/g, "_")}_${date}.pdf`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch { toast.error("PDF download failed"); }
  };

  const filtered = search ? items.filter((i) =>
    (i.visitor_name || "").toLowerCase().includes(search.toLowerCase()) ||
    (i.visitor_phone || "").includes(search.replace(/\D/g, ""))) : items;

  return (
    <section className="space-y-4" data-testid="admin-waiver-records">
      <div className="flex flex-wrap items-center gap-2">
        <Calendar className="w-4 h-4 text-slate-500" />
        <input type="date" value={date} onChange={(e) => setDate(e.target.value)}
               className="px-3 py-2 rounded-xl border border-slate-200 outline-none focus:ring-2 focus:ring-amber-400"
               data-testid="waiver-records-date" />
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name or phone…"
                 className="w-full pl-10 pr-3 py-2 rounded-xl border border-slate-200 outline-none focus:ring-2 focus:ring-amber-400"
                 data-testid="waiver-records-search" />
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden" data-testid="waiver-records-list">
        {loading ? <div className="p-8 text-center text-slate-500">Loading…</div> :
         filtered.length === 0 ? <div className="p-8 text-center text-slate-500">No signed waivers for {date}.</div> : (
          <ul className="divide-y divide-slate-100">
            {filtered.map((s) => (
              <li key={s.signature_id} className="p-4 flex items-center gap-3" data-testid={`waiver-row-${s.signature_id}`}>
                <FileText className="w-5 h-5 text-amber-700 shrink-0" />
                <div className="flex-1 min-w-0">
                  <button onClick={() => openDetail(s.signature_id)} className="font-bold text-slate-900 hover:underline">{s.visitor_name || "—"}</button>
                  <div className="text-xs text-slate-500">{s.visitor_phone_display || s.visitor_phone || "—"} · {fmtDateTime(s.signed_at)} · v{s.waiver_version_number}{s.archived ? " · ARCHIVED" : ""}</div>
                </div>
                <button onClick={() => downloadPdf(s.signature_id, s.visitor_name)}
                        className="text-xs inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-amber-100 hover:bg-amber-200 text-amber-900 font-semibold"
                        data-testid={`waiver-pdf-${s.signature_id}`}>
                  <Download className="w-3.5 h-3.5" /> PDF
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {detail && (
        <div className="fixed inset-0 z-50 bg-black/40 p-4 flex items-center justify-center" onClick={() => setDetail(null)} data-testid="waiver-detail-modal">
          <div className="bg-white rounded-3xl max-w-3xl w-full max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white">
              <h3 className="font-bold">Signed Waiver</h3>
              <button onClick={() => setDetail(null)} className="p-2 hover:bg-slate-100 rounded"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-5">
              <div className="flex items-start gap-4">
                {detail.visitor?.photo_base64 && (
                  <img src={`data:image/jpeg;base64,${detail.visitor.photo_base64}`} alt={detail.visitor.name} className="w-24 h-24 rounded-2xl object-cover" />
                )}
                <div className="flex-1">
                  <div className="text-xl font-bold">{detail.visitor?.name || "—"}</div>
                  <div className="text-sm text-slate-700">{detail.visitor?.phone_display || detail.visitor?.phone || "—"}</div>
                  <div className="text-xs text-slate-500 mt-1">Signed at {fmtDateTime(detail.signature?.signed_at)} · v{detail.signature?.waiver_version_number}</div>
                </div>
                <button onClick={() => downloadPdf(detail.signature_id, detail.visitor?.name)}
                        className="btn-touch inline-flex items-center gap-2 px-3 rounded-xl bg-amber-600 hover:bg-amber-700 text-white font-semibold"
                        data-testid="waiver-detail-pdf">
                  <Download className="w-4 h-4" /> Download PDF
                </button>
              </div>

              <div>
                <div className="text-xs uppercase tracking-wide font-bold text-slate-500 mb-2">Waiver Text (the version they signed)</div>
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-800" style={{ whiteSpace: "pre-wrap", wordWrap: "break-word", maxHeight: "320px", overflowY: "auto" }}>
                  {detail.waiver_version?.text || "(unavailable)"}
                </div>
              </div>

              <div>
                <div className="text-xs uppercase tracking-wide font-bold text-slate-500 mb-2">Signature</div>
                {detail.signature?.signature_image_base64 ? (
                  <img src={`data:image/png;base64,${detail.signature.signature_image_base64}`} alt="signature" className="rounded-xl border border-slate-200 bg-white max-h-48" />
                ) : <div className="text-sm text-slate-500">(no image stored)</div>}
              </div>

              <div className="text-xs text-slate-500 font-mono space-y-0.5">
                <div>IP: {detail.signature?.ip_address || "—"}</div>
                <div>UA: {detail.signature?.user_agent || "—"}</div>
                {detail.signature?.archived_at && <div className="text-amber-700">Archived at {detail.signature.archived_at}</div>}
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

export default function AdminWaiverPanel({ subTab }) {
  if (subTab === "records") return <WaiverRecordsPanel />;
  return <WaiverSettingsPanel />;
}
